import React, { useState, useMemo } from 'react';
import { 
  Box, Button, Autocomplete, TextField, Stack, 
  ToggleButtonGroup, ToggleButton, Typography, 
  IconButton, FormControl, Select, MenuItem,
  createFilterOptions
} from '@mui/material';
import { Close, FilterList } from '@mui/icons-material';
import { useCRMTagsContext } from '../../CRMTagsContext';
import { useCRMGeoContext } from '../../CRMGeoContext';
import { PseudoLink2, SimpleDialog, useDialogState } from '../../../primitives';
import { CustomAttributeType, Tag } from '../../types';
import { IFilters } from '../../data/useContactsList';
import { TagChip, TagChipBare } from '../CRMTags';
import { FormControlsForFields } from '../../../schemed';
import { pluralizeRu } from '../../../../api/pluralize';
import { FieldType, mergeSchema, Schema } from '../../../../hooks/useSchema';
import { useContactAttributesConfig } from '../../data/useContactAttributesConfig';
import { ExtraProps } from '../../../schemed/FormControlProps';
import { AutoSuggestMultiple } from '../../../primitives/AutoSuggests';
import { crmAttributeSuggestionsKey } from './CustomAttributes/CustomAttributesEditors';

export interface ContactsFiltersPickerProps {
  filters?: Partial<IFilters> & { attributes?: any[] };
  onUpdateFilters: (changes: Partial<IFilters> & { attributes?: any[] }) => void;
  clearFilters: () => void;
  filteredRecordsCount?: number;
  contactSchema: Schema;
}

const filter = createFilterOptions<Tag>();

// Helper for the two-column row layout
const FilterRow = ({ 
  label, 
  leftContent, 
  control, 
  onRemove 
}: { 
  label?: string, 
  leftContent?: React.ReactNode, 
  control: React.ReactNode,
  onRemove?: () => void 
}) => (
  <Box display="grid" gridTemplateColumns="1fr 2fr" sx={{ borderTop: "1px solid #cccccc80", paddingTop: 1 }} gap={2} alignItems="center">
    <Box display="flex" alignItems="center" gap={0.5}>
      {leftContent || (
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      )}
      {onRemove && (
        <PseudoLink2 onClick={onRemove} style={{ alignSelf: "flex-start", marginTop: 1, color: "inherit", opacity: 0.35 }}>
          <Close fontSize="inherit" />
        </PseudoLink2>
      )}
    </Box>
    <Box>{control}</Box>
  </Box>
);

// Configuration for core optional filters
const OPTIONAL_FILTERS = [
  { key: 'geo_country', label: 'Страны' },
  { key: 'geo_city', label: 'Города' },
  { key: 'email_search', label: 'Поиск по e-mail' },
  { key: 'languages', label: 'Языки общения' },
  { key: 'consent_pd', label: 'Согласие ПД' },
  { key: 'consent_emails', label: 'Согласие рассылок' },
  { key: 'has_phone', label: 'Телефон' },
  { key: 'has_email', label: 'Email' },
  { key: 'do_not_contact', label: 'Не контактировать' },
] as const;

export const ContactsFiltersPicker = ({ filters = {}, onUpdateFilters, clearFilters, filteredRecordsCount, contactSchema }: ContactsFiltersPickerProps) => {
  const { tags: contextTags } = useCRMTagsContext();
  const { countries, cities } = useCRMGeoContext();
  const dialog = useDialogState();
  
  const [searchValue, setSearchValue] = useState("");
  const [revealedFilters, setRevealedFilters] = useState<string[]>([]);

  const { attributes: customAttributes = [], schema: customSchemaBase } = useContactAttributesConfig();
  const customSchema = useMemo(() => {
    const cs = { ...customSchemaBase };
    customAttributes.forEach(attr => {
      if(attr.type === FieldType.select) {
        cs[attr.code].type = FieldType.multiselect;
      }
    })
    return cs;
  }, [customSchemaBase, customAttributes]);

  // Default fallbacks
  const tagIds = filters.tags || [];
  const mode = filters.mode || 'OR';
  const selectedCountries = filters.geo_country || [];
  const selectedCities = filters.geo_city || [];
  const customFiltersActive = filters.attributes || [];

  // Count active filters for the badge
  let activeFilterCount = tagIds.length + selectedCountries.length + selectedCities.length + customFiltersActive.length;
  if (filters.categories?.length) activeFilterCount++;
  if (filters.has_phone) activeFilterCount++;
  if (filters.has_email) activeFilterCount++;
  if (filters.do_not_contact) activeFilterCount++;
  if (filters.consent_pd?.length) activeFilterCount++;
  if (filters.consent_emails?.length) activeFilterCount++;
  if (filters.email_search?.length) activeFilterCount++;

  const handleRemoveTag = (e: React.MouseEvent, tagToRemove: Tag) => {
    e.stopPropagation();
    onUpdateFilters({ tags: tagIds.filter(id => id.toString() !== tagToRemove._id.toString()) });
  };

  const handleSelectTag = (newValue: Tag | string | null) => {
    if (!newValue || typeof newValue === 'string') return;
    const selectedId = newValue._id;
    if (!tagIds.some(id => id.toString() === selectedId.toString())) {
      onUpdateFilters({ tags: [...tagIds, selectedId] });
    }
    setSearchValue("");
  };

  const availableTags = contextTags.filter(
    t => !tagIds.some(id => id.toString() === t._id.toString())
  );

  const filteredOptions = useMemo(() => {
    return filter(availableTags, { inputValue: searchValue, getOptionLabel: (o) => o.name });
  }, [searchValue, availableTags]);

  const schema = useMemo(() => {
    return mergeSchema(contactSchema, {
      email_search: { label: "Поиск по e-mail"},
      consent_pd: { type: FieldType.multiselect },
      consent_emails: { type: FieldType.multiselect },
      languages: contactSchema.communication_language,
    })
  }, [contactSchema]);

  // Determine if a standard optional filter should be visible
  const isFilterVisible = (key: keyof IFilters) => {
    if (revealedFilters.includes(key as string)) return true;
    const val = filters[key];
    if (Array.isArray(val)) return val.length > 0;
    if (typeof val === 'boolean') return val !== undefined;
    return !!val;
  };

  // Determine if a custom attribute filter should be visible
  const isCustomFilterVisible = (code: string) => {
    if (revealedFilters.includes(`custom_${code}`)) return true;
    return customFiltersActive.some(f => f.attribute === code);
  };

  // Build the array of all available filters (standard + custom)
  const availableOptionalFilters = [
    ...OPTIONAL_FILTERS.filter(f => !isFilterVisible(f.key as keyof IFilters)).map(f => ({ ...f, type: 'standard' })),
    ...customAttributes.filter(a => !isCustomFilterVisible(a.code)).map(a => ({ key: `custom_${a.code}`, label: a.label, type: 'custom', code: a.code }))
  ];

  // Helper to render FormControlsForFields without the label (to fit the right column cleanly)
  const renderSchemedControl = (fieldKey: string, autoComplete = false) => (
    <FormControlsForFields
      data={filters}
      onChange={(o,c) => onUpdateFilters(c)}
      schema={{...schema, [fieldKey]: { ...schema[fieldKey], label: '' }}}
      fields={[[fieldKey, { autoComplete, label: " " }]]}
    />
  );

  // Formatter for custom filter chip values
  const formatCustomFilterValue = (val: any) => Array.isArray(val) ? val.join(', ') : String(val);

  // Remove a standard filter completely
  const handleRemoveStandardFilter = (fieldKey: keyof IFilters) => {
    setRevealedFilters(prev => prev.filter(k => k !== fieldKey));
    // Provide appropriate empty values based on key
    const resetValue = ['geo_country', 'geo_city', 'consent_pd', 'consent_emails', 'languages'].includes(fieldKey) ? [] : "";
    onUpdateFilters({ [fieldKey]: resetValue });
  };

  // ============================================================================
  // UNIFIED LOGIC FOR CUSTOM ATTRIBUTE UPDATES
  // ============================================================================
  
  // Update the actual value of a custom attribute filter
  const handleUpdateCustomAttribute = (attr: any, val: any, isNegated: boolean) => {
    const otherFilters = customFiltersActive.filter((f: any) => f.attribute !== attr.code);
    
    // If value is cleared completely, just remove the filter
    if (val === undefined || val === null || val === "" || (Array.isArray(val) && val.length === 0)) {
      onUpdateFilters({ attributes: otherFilters });
      return;
    }

    const FilterOpByAttrType = {
      [FieldType.select]: "in",
      [CustomAttributeType.suggest]: "in",
      [CustomAttributeType.suggest_multi]: "array_intersects",
      [FieldType.text]: "ilike",
      [FieldType.textlong]: "ilike",
      default: "eq"
    }

    const isOrNilOnNegation = !attr.type
      || [FieldType.text, FieldType.textlong, CustomAttributeType.suggest, CustomAttributeType.suggest_multi]
        .includes(attr.type as any)

    onUpdateFilters({ 
      attributes: [
        ...otherFilters, 
        {
          attribute: attr.code,
          op: FilterOpByAttrType[attr.type || FieldType.text] || FilterOpByAttrType.default,
          not: isNegated,
          value: val,
          or_nil: false
            || (attr.type === FieldType.bool && val === false)
            || (isOrNilOnNegation && !!isNegated),
        }
      ] 
    });
  };

  // Update the negation (EQ vs NOT_EQ) state of an existing custom attribute filter
  const handleToggleCustomAttributeNegation = (attr: any, currentFilter: any, isNegated: boolean) => {
    if (!currentFilter) return; // Cannot toggle negation if no value is set
    handleUpdateCustomAttribute(attr, currentFilter.value, isNegated);
  };
  // ============================================================================

  return (
    <Box>
      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
        {/* Main Filter Trigger */}
        <Button
          size="small"
          startIcon={<FilterList />}
          onClick={() => dialog.open()}
          sx={{
            textTransform: 'none',
            borderRadius: 4,
            height: 24,
            px: 1,
            py: 0,
            color: activeFilterCount > 0 ? 'primary.main' : 'text.secondary',
            borderColor: activeFilterCount > 0 ? 'primary.main' : 'transparent',
            border: activeFilterCount > 0 ? '1px solid' : "none",
            bgcolor: activeFilterCount > 0 ? 'primary.50' : 'transparent',
            '&:hover': { borderColor: 'primary.dark' }
          }}
        >
          Фильтры {activeFilterCount > 0 && `(${activeFilterCount})`}
        </Button>
        
        {/* Inline chips for quick removal */}
        {tagIds.map((id) => (
          <TagChip 
            key={`tag_${id}`} 
            tagId={id} 
            onClick={() => dialog.open()}
            action={handleRemoveTag} 
            actionIcon={<Close sx={{ fontSize: 12 }} />} 
          />
        ))}
        {selectedCountries.map(c => (
          <TagChipBare
            key={c}
            tag={{ _id: 0, name: c } as any}
            action={() => onUpdateFilters({ geo_country: selectedCountries.filter(x => x !== c) })}
            actionIcon={<Close sx={{ fontSize: 12 }} />}
          />
        ))}
        {selectedCities.map(c => (
          <TagChipBare
            key={c}
            tag={{ _id: 0, name: c } as any}
            action={() => onUpdateFilters({ geo_city: selectedCities.filter(x => x !== c) })}
            actionIcon={<Close sx={{ fontSize: 12 }} />}
          />
        ))}
        {!!filters.email_search?.length && (
          <TagChipBare
            tag={{ _id: 0, name: `email: ${filters.email_search}` } as any}
            action={() => onUpdateFilters({ email_search: "" })}
            actionIcon={<Close sx={{ fontSize: 12 }} />}
          />
        )}
        
        {/* Chips for custom attributes */}
        {customFiltersActive.map((f: any) => {
          const attr = customAttributes.find(a => a.code === f.attribute);
          const label = attr ? `${attr.label} ${f.not ? '!=' : '='} ${formatCustomFilterValue(f.value)}` : f.attribute;
          return (
            <TagChipBare
              key={`ca_${f.attribute}`}
              tag={{ _id: 0, name: label } as any}
              action={() => {
                const newAttrFilters = customFiltersActive.filter(x => x.attribute !== f.attribute);
                onUpdateFilters({ attributes: newAttrFilters });
              }}
              actionIcon={<Close sx={{ fontSize: 12 }} />}
            />
          );
        })}

        {activeFilterCount > 0 && (
          <IconButton size="small" onClick={() => { clearFilters(); setRevealedFilters([]); }}>
            <Close />
          </IconButton>
        )}
      </Stack>

      {/* DIALOG VIEW */}
      {dialog.isOpen && (
        <SimpleDialog
          {...dialog}
          dialogTitle="Фильтры контактов"
          titleActions={<>
            <Typography variant="caption" color="textSecondary" sx={{ alignSelf: "center" }}>
              {filteredRecordsCount || 0} {pluralizeRu(filteredRecordsCount || 0, "контакт", "контакта", "контактов")}
            </Typography>
          </>}
          maxWidth="sm"
          noFullscreen
          noSave
          noCancel
          save={() => {}}
          simpleExtraActions={<>
            <Button size="small" startIcon={<Close />} onClick={() => { clearFilters(); setRevealedFilters([]); }}>
              сбросить
            </Button>
            <Button size="small" color="primary" onClick={() => dialog.close()}>
              закрыть
            </Button>
          </>}
        >
          <Stack spacing={1} mt={1}>

            {/* TAGS FILTER (Always visible) */}
            <FilterRow 
              leftContent={
                <Stack alignItems="flex-start" gap={1}>
                  <Typography variant="body2" color="text.secondary">Теги</Typography>
                  <ToggleButtonGroup
                    value={mode}
                    exclusive
                    onChange={(e, val) => val && onUpdateFilters({ mode: val })}
                    size="small"
                    sx={{ height: 28 }}
                  >
                    <ToggleButton value="OR" sx={{ px: 1, py: 0, fontSize: '0.75rem', textTransform: 'none' }}>ИЛИ</ToggleButton>
                    <ToggleButton value="AND" sx={{ px: 1, py: 0, fontSize: '0.75rem', textTransform: 'none' }}>И</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              }
              control={
                <Box>
                  <Autocomplete
                    options={filteredOptions}
                    getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
                    inputValue={searchValue}
                    onInputChange={(e, val) => setSearchValue(val)}
                    value={null}
                    onChange={(e, val) => handleSelectTag(val)}
                    filterOptions={(x) => x}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        autoFocus
                        size="small" 
                        placeholder="Добавить тег..." 
                        onKeyDown={(e) => {
                          if (params.inputProps.onKeyDown) params.inputProps.onKeyDown(e as any);
                          if (e.key === 'Enter' && searchValue.trim() !== '') {
                            if (filteredOptions.length === 1) {
                              e.preventDefault();
                              handleSelectTag(filteredOptions[0]);
                            }
                          }
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option._id}>
                        <TagChipBare tag={option} />
                      </li>
                    )}
                  />
                  {tagIds.length > 0 && (
                    <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
                      {tagIds.map((id) => (
                        <TagChip 
                          key={id} 
                          tagId={id} 
                          action={handleRemoveTag} 
                          actionIcon={<Close sx={{ fontSize: 12 }} />} 
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              }
            />

            {/* CATEGORIES FILTER (Always visible) */}
            <FilterRow 
              label="Категории"
              control={renderSchemedControl("categories", true)}
            />

            {/* CONDITIONAL FILTERS (Standard) */}
            {isFilterVisible('geo_country') && (
              <FilterRow 
                label="Страны"
                onRemove={() => handleRemoveStandardFilter('geo_country')}
                control={
                  <Autocomplete
                    multiple
                    size="small"
                    options={countries}
                    value={selectedCountries}
                    onChange={(e, val) => onUpdateFilters({ geo_country: val })}
                    renderInput={(params) => <TextField {...params} placeholder="Выберите страны" />}
                  />
                }
              />
            )}

            {isFilterVisible('geo_city') && (
              <FilterRow 
                label="Города"
                onRemove={() => handleRemoveStandardFilter('geo_city')}
                control={
                  <Autocomplete
                    multiple
                    size="small"
                    options={cities}
                    value={selectedCities}
                    onChange={(e, val) => onUpdateFilters({ geo_city: val })}
                    renderInput={(params) => <TextField {...params} placeholder="Выберите города" />}
                  />
                }
              />
            )}

            {isFilterVisible('email_search') && (
              <FilterRow 
                label="Поиск по e-mail" 
                onRemove={() => handleRemoveStandardFilter('email_search')}
                control={renderSchemedControl("email_search")} 
              />
            )}

            {isFilterVisible('languages') && (
              <FilterRow 
                label="Языки общения" 
                onRemove={() => handleRemoveStandardFilter('languages')}
                control={renderSchemedControl("languages", true)} 
              />
            )}

            {isFilterVisible('consent_pd') && (
              <FilterRow 
                label="Согласие ПД" 
                onRemove={() => handleRemoveStandardFilter('consent_pd')}
                control={renderSchemedControl("consent_pd", true)} 
              />
            )}

            {isFilterVisible('consent_emails') && (
              <FilterRow 
                label="Согласие рассылок" 
                onRemove={() => handleRemoveStandardFilter('consent_emails')}
                control={renderSchemedControl("consent_emails", true)} 
              />
            )}

            {isFilterVisible('has_phone') && (
              <FilterRow 
                label="Телефон"
                onRemove={() => handleRemoveStandardFilter('has_phone')}
                control={
                  <FormControl size="small" fullWidth>
                    <Select
                      displayEmpty
                      value={filters.has_phone || ''}
                      onChange={(e) => onUpdateFilters({ has_phone: e.target.value as any })}
                    >
                      <MenuItem value=""><em>Все</em></MenuItem>
                      <MenuItem value="true">Есть телефон</MenuItem>
                      <MenuItem value="false">Нет телефона</MenuItem>
                    </Select>
                  </FormControl>
                }
              />
            )}

            {isFilterVisible('has_email') && (
              <FilterRow 
                label="Email"
                onRemove={() => handleRemoveStandardFilter('has_email')}
                control={
                  <FormControl size="small" fullWidth>
                    <Select
                      displayEmpty
                      value={filters.has_email || ''}
                      onChange={(e) => onUpdateFilters({ has_email: e.target.value as any })}
                    >
                      <MenuItem value=""><em>Все</em></MenuItem>
                      <MenuItem value="true">Есть email</MenuItem>
                      <MenuItem value="false">Нет email</MenuItem>
                    </Select>
                  </FormControl>
                }
              />
            )}

            {isFilterVisible('do_not_contact') && (
              <FilterRow 
                label="Не контактировать"
                onRemove={() => handleRemoveStandardFilter('do_not_contact')}
                control={
                  <FormControl size="small" fullWidth>
                    <Select
                      displayEmpty
                      value={filters.do_not_contact || ''}
                      onChange={(e) => onUpdateFilters({ do_not_contact: e.target.value as any })}
                    >
                      <MenuItem value=""><em>Все</em></MenuItem>
                      <MenuItem value="only">Только с запретом</MenuItem>
                      <MenuItem value="exclude">Только без запрета</MenuItem>
                    </Select>
                  </FormControl>
                }
              />
            )}

            {/* Custom Attributes */}
            {customAttributes.filter(a => isCustomFilterVisible(a.code)).map(attr => {
              const currentFilter = customFiltersActive.find((f: any) => f.attribute === attr.code);
              // Create a mock flat data object to pass to FormControlsForFields
              const mockData = { [attr.code]: currentFilter?.value };

              const options: ExtraProps = { autoComplete: true, label: " " };
              if((attr.type as any) === CustomAttributeType.suggest || (attr.type as any) === CustomAttributeType.suggest_multi) {
                options.control = AutoSuggestMultiple;
                options.suggestionsKey = crmAttributeSuggestionsKey(attr.code);
              }

              return (
                <FilterRow 
                  key={`custom_${attr.code}`}
                  onRemove={() => {
                    setRevealedFilters(prev => prev.filter(k => k !== `custom_${attr.code}`));
                    onUpdateFilters({
                      attributes: customFiltersActive.filter(f => f.attribute !== attr.code)
                    });
                  }}
                  leftContent={
                    <Stack alignItems="flex-start" gap={0.5}>
                      <Typography variant="body2" color="text.secondary">{attr.label}</Typography>

                      {attr.type !== FieldType.bool &&
                        <ToggleButtonGroup
                          value={currentFilter?.not ? "NOT_EQ" : "EQ"}
                          exclusive
                          onChange={(e, val) => {
                            if (!val) return;
                            handleToggleCustomAttributeNegation(attr, currentFilter, val === "NOT_EQ");
                          }}
                          size="small"
                          sx={{ height: 24 }}
                        >
                          <ToggleButton value="EQ" sx={{ px: 1, py: 0, fontSize: '0.7rem', textTransform: 'none' }}>=</ToggleButton>
                          <ToggleButton value="NOT_EQ" sx={{ px: 1, py: 0, fontSize: '0.7rem', textTransform: 'none' }}>НЕ</ToggleButton>
                        </ToggleButtonGroup>}
                    </Stack>
                  }
                  control={
                    <FormControlsForFields
                      data={mockData}
                      onChange={(o, c) => {
                        const isNegated = !!currentFilter?.not;
                        handleUpdateCustomAttribute(attr, c[attr.code], isNegated);
                      }}
                      schema={customSchema}
                      fields={[[attr.code, options]]}
                    />
                  }
                />
              );
            })}

            {/* BUTTONS TO ADD INACTIVE FILTERS */}
            {availableOptionalFilters.length > 0 && (
              <Box pt={1}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Добавить фильтр:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {availableOptionalFilters.map(f => (
                    <Button 
                      key={f.key} 
                      variant="text" 
                      size="small" 
                      onClick={() => setRevealedFilters(prev => [...prev, f.key])}
                      sx={{ textTransform: 'none', borderRadius: 4, bgcolor: 'action.hover' }}
                    >
                      + {f.label}
                    </Button>
                  ))}
                </Box>
              </Box>
            )}

          </Stack>
        </SimpleDialog>
      )}
    </Box>
  );
};